import React, { useState, useEffect, useRef, useCallback } from 'react';
import { DashboardWidget } from './DashboardWidget';
import { TableWidget, TableRow } from './TableWidget';
import { PlusIcon } from './PlusIcon';
import { InfoModal } from './InfoModal';
import { TokenBalancesWidget } from './TokenBalancesWidget';
import { PoolPositionsWidget } from './PoolPositionsWidget';
import { CoinbaseBalancesWidget } from './CoinbaseBalancesWidget';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { fetchUserAccount, type UserAccountResponse } from '../../api/userAccount';
import { fetchUserCoinbaseHoldings, type CoinbaseHolding } from '../../api/coinbaseHoldings';
import type { UserPositionGroup } from '../../api/types';
import { fetchUserTokenBalance, fetchTokenPrice, fetchNativeEthBalance } from '../../api/userPositions';
import { useUserPositions } from '../../hooks/useUserPositions';
import { useConfig } from '../../hooks/useConfig';
import { TOKENS } from '@sepori/shared/registry';
import { formatFromWei } from '../../api/userPositions';

export const InvestmentSummaryWidget: React.FC<{ className?: string }> = ({ className }) => {
  const { user, isAuthenticated } = useAuth();
  const { widgetAutoRefreshIntervalMs } = useConfig();
  const authToken = localStorage.getItem('authToken') || '';
  const [walletModalOpen, setWalletModalOpen] = useState(false);
  const [poolModalOpen, setPoolModalOpen] = useState(false);
  const [coinbaseModalOpen, setCoinbaseModalOpen] = useState(false);
  const [rows, setRows] = useState<TableRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [timeoutError, setTimeoutError] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timeoutStarted = useRef<boolean>(false);
  const isRefreshingRef = useRef<boolean>(false);
  const isLoadingDataRef = useRef<boolean>(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { data: userAccount, isLoading: isLoadingAccount, error: errorAccount, refetch: refetchUserAccount } = useQuery({
    queryKey: ['userAccount', user?.id],
    queryFn: () => fetchUserAccount(user!.id),
    enabled: isAuthenticated && !!user?.id,
  });

  const { data: coinbaseHoldings, isLoading: isLoadingCoinbase, error: errorCoinbase, refetch: refetchCoinbase } = useQuery({
    queryKey: ['coinbaseHoldings', user?.id],
    queryFn: () => fetchUserCoinbaseHoldings(user!.id, authToken),
    enabled: isAuthenticated && !!user?.id,
  });

  const { positionGroups, loading: isLoadingPositions, error: errorPositions, refreshCurrentData: refetchPositions } = useUserPositions(user?.walletAddress || null);

  // Extract loadData into a reusable function
  const processAndUpdateData = useCallback(async (
    skipGuardChecks = false,
    freshUserAccount?: UserAccountResponse,
    freshCoinbaseHoldings?: CoinbaseHolding[],
    freshPositionGroups?: UserPositionGroup[],
    bypassCache = false
  ) => {
    // Guard checks for normal useEffect flow
    if (!skipGuardChecks) {
      if (!user?.walletAddress || isLoadingAccount || isLoadingCoinbase || isLoadingPositions) return;

      // Skip if we're in the middle of a manual refresh (only during useEffect)
      if (isRefreshingRef.current) {
        return;
    }

      // Prevent concurrent executions
      if (isLoadingDataRef.current) {
        return;
      }
    }

    // During refresh, check if we're still loading
    if (!skipGuardChecks && (isLoadingAccount || isLoadingCoinbase || isLoadingPositions)) {
      return;
    }

      try {
      isLoadingDataRef.current = true;
          setLoading(true);
        setError(null);

      // Use fresh data if provided (from refetch), otherwise use hook data
      const currentUserAccount = freshUserAccount ?? userAccount;
      const currentCoinbaseHoldings = freshCoinbaseHoldings ?? coinbaseHoldings;
      const currentPositionGroups = freshPositionGroups ?? positionGroups;

      // Track which data sources are available
      const hasUserAccount = !errorAccount && currentUserAccount;
      const hasCoinbaseHoldings = !errorCoinbase && currentCoinbaseHoldings;
      const hasPositions = !errorPositions && currentPositionGroups;

      // Store wallet address in a local variable
      const walletAddress = user?.walletAddress;
      if (!walletAddress) {
        return;
      }

        // Calculate wallet balance (sum all token balances + bank balance + veAERO value)
        let walletBalance = 0;
      let walletBalanceError = false;

      // Add bank balance first (if userAccount is available)
      // Note: walletBalance can still be calculated from token balances even if userAccount is missing
      if (hasUserAccount) {
        try {
          const bankBalance = Number(currentUserAccount.account.bank_balance_usd);
        if (bankBalance > 0) {
          walletBalance += bankBalance;
          }
        } catch (e) {
          console.warn('Failed to process bank balance:', e);
          // Don't set error - wallet balance can still be calculated from tokens
        }
        }

        const tokenMetas = Object.values(TOKENS).sort((a, b) => a.display_order - b.display_order);

        // Step 1: Fetch all token balances in parallel
        const tokenBalancePromises = tokenMetas.map(async (tokenMeta) => {
          try {
            if (tokenMeta.symbol === 'ETH') {
              const ethBalanceData = await fetchNativeEthBalance(walletAddress);
              return {
                tokenMeta,
                balance: formatFromWei(ethBalanceData.balance, tokenMeta.decimals)
              };
            } else {
              const tokenBalanceData = await fetchUserTokenBalance(tokenMeta.address, walletAddress);
              return {
                tokenMeta,
                balance: formatFromWei(tokenBalanceData.balance, tokenMeta.decimals)
              };
            }
          } catch (e) {
            console.warn(`Failed to fetch balance for ${tokenMeta.symbol}:`, e);
            return { tokenMeta, balance: 0 };
          }
        });

        const tokenBalances = await Promise.all(tokenBalancePromises);

        // Step 2: Collect all unique token addresses that need prices
        const uniqueTokenAddresses = new Set<string>();

        // Add tokens with non-zero balances
        tokenBalances.forEach(({ tokenMeta, balance }) => {
          if (balance > 0) {
            uniqueTokenAddresses.add(tokenMeta.address);
          }
        });

        // Add AERO (needed for veAERO and claimable rewards)
        const AERO_ADDRESS = TOKENS.AERO.address;
        uniqueTokenAddresses.add(AERO_ADDRESS);

        // Add coinbase holding tokens
      if (currentCoinbaseHoldings) {
        currentCoinbaseHoldings.forEach(holding => {
            // USD is fiat, skip price fetching (use $1 directly in calculation)
            if (holding.token_symbol === 'USD') {
              return; // Skip adding to uniqueTokenAddresses, will handle USD separately
            }
            const tokenMeta = Object.values(TOKENS).find(t => t.symbol === holding.token_symbol);
            if (tokenMeta) {
              uniqueTokenAddresses.add(tokenMeta.address);
            }
          });
        }

        // Step 3: Fetch all unique token prices in parallel (fresh prices every execution)
        const pricePromises = Array.from(uniqueTokenAddresses).map(async (address) => {
          try {
            const priceData = await fetchTokenPrice(address, { bypassCache });
            return { address, priceUsd: priceData.priceUsd };
          } catch (e) {
            console.warn(`Failed to fetch price for ${address}:`, e);
            return { address, priceUsd: 0 };
          }
        });

        const priceResults = await Promise.all(pricePromises);

        // Step 4: Create temporary price map for this execution only
        const priceMap = new Map<string, number>();
        priceResults.forEach(({ address, priceUsd }) => {
          priceMap.set(address.toLowerCase(), priceUsd);
        });

        // Step 5: Calculate wallet balance using price map
        tokenBalances.forEach(({ tokenMeta, balance }) => {
          if (balance > 0) {
            const price = priceMap.get(tokenMeta.address.toLowerCase()) || 0;
            walletBalance += balance * price;
          }
        });

      // Add veAERO value using cached AERO price (if userAccount is available)
      if (hasUserAccount) {
        try {
          const veAeroAmount = Number((currentUserAccount.account as any).veaero_amount ?? 0);
          if (veAeroAmount > 0) {
            const aeroPrice = priceMap.get(AERO_ADDRESS.toLowerCase()) || 0;
            walletBalance += veAeroAmount * aeroPrice;
          }
        } catch (e) {
          console.warn('Failed to include veAERO value in wallet balance:', e);
          // Don't set error - wallet balance can still be calculated from tokens
        }
        }

      // Only set walletBalanceError if there was an actual error in token balance fetching
      // (This would be caught by the token balance promise catch blocks, but we don't track that here)
      // For now, walletBalanceError remains false unless we add error tracking to token balance fetching

        // Calculate pool balance (deposits + claimable rewards)
        let poolBalance = 0;
      let poolBalanceError = false;
        const aeroPrice = priceMap.get(AERO_ADDRESS.toLowerCase()) || 0;

      if (hasPositions) {
        try {
          for (const group of currentPositionGroups!) {
            for (const position of group.positions) {
              poolBalance += position.positionValueUsd;
              // Add claimable rewards if available (using cached AERO price)
              if (position.claimableEmissions && position.claimableEmissions > 0) {
                poolBalance += position.claimableEmissions * aeroPrice;
              }
            }
          }
        } catch (e) {
          console.warn('Failed to calculate pool balance:', e);
          poolBalanceError = true;
        }
      } else {
        poolBalanceError = !hasPositions;
        }

        // Calculate coinbase balance using cached prices
        let coinbaseBalance = 0;
      let coinbaseBalanceError = false;
      if (hasCoinbaseHoldings) {
        try {
          currentCoinbaseHoldings!.forEach((holding) => {
            try {
              // Handle USD separately (fiat currency, fixed $1 price)
              if (holding.token_symbol === 'USD') {
                coinbaseBalance += holding.amount * 1; // USD is always $1
                return;
              }

              const tokenMeta = Object.values(TOKENS).find(t => t.symbol === holding.token_symbol);
              if (tokenMeta) {
                const price = priceMap.get(tokenMeta.address.toLowerCase()) || 0;
                coinbaseBalance += holding.amount * price;
              }
            } catch (e) {
              console.warn(`Failed to process coinbase holding for ${holding.token_symbol}:`, e);
            }
          });
        } catch (e) {
          console.warn('Failed to calculate coinbase balance:', e);
          coinbaseBalanceError = true;
        }
      } else {
        coinbaseBalanceError = !hasCoinbaseHoldings;
      }

      // Calculate totals (only if we have valid data)
      let currentValue = 0;
      let currentValueError = false;
      let totalGainLoss = 0;
      let totalGainLossError = false;

      if (walletBalanceError || poolBalanceError || coinbaseBalanceError) {
        currentValueError = true;
      } else {
        currentValue = walletBalance + poolBalance + coinbaseBalance;
      }

      if (hasUserAccount && !currentValueError) {
        try {
          totalGainLoss = currentValue - currentUserAccount.account.total_invested_usd;
        } catch (e) {
          console.warn('Failed to calculate gain/loss:', e);
          totalGainLossError = true;
        }
      } else {
        totalGainLossError = !hasUserAccount || currentValueError;
      }

        // Helper function to format negative numbers with minus sign before dollar
        const formatCurrency = (value: number) => {
          if (value < 0) {
            return `-$${Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          }
          return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        };

      // Create rows with error handling
        const newRows: TableRow[] = [
          {
            label: 'Total Invested',
            value: hasUserAccount
              ? `$${Number(currentUserAccount.account.total_invested_usd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : 'Error',
          valueClass: !hasUserAccount ? 'error' : undefined
          },
          {
            label: 'Wallet Balance',
          value: walletBalanceError
            ? 'Error'
            : `$${walletBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          valueClass: walletBalanceError ? 'error' : undefined,
            infoIcon: <PlusIcon onClick={() => setWalletModalOpen(true)} title="Click to view wallet token balances" />
          },
          {
            label: 'Pool Balance',
          value: poolBalanceError
            ? 'Error'
            : `$${poolBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          valueClass: poolBalanceError ? 'error' : undefined,
            infoIcon: <PlusIcon onClick={() => setPoolModalOpen(true)} title="Click to view pool deposits and claimable values" />
          },
          {
            label: 'Coinbase Balance',
          value: coinbaseBalanceError
            ? 'Error'
            : `$${coinbaseBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          valueClass: coinbaseBalanceError ? 'error' : undefined,
            infoIcon: <PlusIcon
              onClick={() => setCoinbaseModalOpen(true)}
              title="Click to view Coinbase token balances"
            />
          },
          {
            label: 'Current Value',
          value: currentValueError
            ? 'Error'
            : `$${currentValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          valueClass: currentValueError ? 'error' : undefined
          },
          {
            label: 'Current Gain/Loss',
          value: totalGainLossError
            ? 'Error'
            : formatCurrency(totalGainLoss),
          valueClass: totalGainLossError
            ? 'error'
            : (totalGainLoss >= 0 ? 'positive bold' : 'negative bold'),
            labelClass: 'bold'
          }
        ];

        setRows(newRows);
        setLastUpdated(new Date());
      } catch (err) {
        console.error('Failed to load investment summary data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load investment summary data');
      } finally {
        setLoading(false);
      isLoadingDataRef.current = false;
      }
  }, [user?.walletAddress, userAccount, coinbaseHoldings, positionGroups, errorAccount, errorCoinbase, errorPositions, isLoadingAccount, isLoadingCoinbase, isLoadingPositions]);

  const refreshData = async () => {
    // Immediately set loading state to prevent button animation
    setLoading(true);
    setRefreshing(true);
    isRefreshingRef.current = true;
    refreshJustCompletedRef.current = false;
    try {
      // Capture refetch results to get fresh data immediately
      const [userAccountResult, coinbaseResult, positionGroupsResult] = await Promise.all([
        refetchUserAccount(),
        refetchCoinbase(),
        refetchPositions()
      ]);

      // Extract fresh data from refetch results
      // React Query refetch returns { data, error, isError, ... }
      const freshUserAccount = userAccountResult.data;
      const freshCoinbaseHoldings = coinbaseResult.data;
      const freshPositionGroups = positionGroupsResult; // This is already the array from our modified hook

      // All refetches completed - now process the data directly with fresh data
      // This ensures we only run once after all queries complete, using fresh data
      await processAndUpdateData(
        true, // skip guard checks
        freshUserAccount,
        freshCoinbaseHoldings,
        freshPositionGroups,
        true // bypassCache=true for manual refresh
      );

      // Set flag to prevent useEffect from running immediately after
      // Clear it after a short delay to allow normal updates
      refreshJustCompletedRef.current = true;
      setTimeout(() => {
        refreshJustCompletedRef.current = false;
      }, 100);
    } catch (err) {
      console.error('Failed to refresh data:', err);
    } finally {
      setRefreshing(false);
      isRefreshingRef.current = false;
    }
  };

  // Start timeout when component starts loading - only run once when loading starts
  useEffect(() => {
    // Only start timeout if we're loading, haven't started timeout yet, and haven't already timed out
    if ((isLoadingAccount || isLoadingCoinbase || isLoadingPositions) && !timeoutStarted.current && !timeoutError) {
      timeoutStarted.current = true;
      timeoutRef.current = setTimeout(() => {
        setTimeoutError(true);
      }, 30000); // 30 seconds
    } else if (!isLoadingAccount && !isLoadingCoinbase && !isLoadingPositions && timeoutRef.current) {
      // Clear timeout if not loading
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
      timeoutStarted.current = false;
    }

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
        timeoutStarted.current = false;
      }
    };
  }, [isLoadingAccount, isLoadingCoinbase, isLoadingPositions, timeoutError]);

  // Track when refresh just completed to prevent immediate useEffect execution
  const refreshJustCompletedRef = useRef<boolean>(false);

  useEffect(() => {
    // Don't run if queries are still loading
    if (!user?.walletAddress || isLoadingAccount || isLoadingCoinbase || isLoadingPositions) return;

    // Skip if we're in the middle of a manual refresh
    if (isRefreshingRef.current) {
      return;
    }

    // Skip if refresh just completed (debounce effect to prevent double execution)
    if (refreshJustCompletedRef.current) {
      return;
    }

    // Normal data flow - process and update
    processAndUpdateData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.walletAddress, userAccount, coinbaseHoldings, positionGroups, errorAccount, errorCoinbase, errorPositions, processAndUpdateData]);

  // Auto-refresh every 5 minutes
  useEffect(() => {
    // Only set up auto-refresh if user is authenticated and has wallet address
    if (!user?.walletAddress || !isAuthenticated) {
      return;
    }

    // Set up auto-refresh interval
    intervalRef.current = setInterval(() => {
      // Only refresh if not currently loading or refreshing
      if (!isRefreshingRef.current && !isLoadingDataRef.current) {
        refreshData();
      }
    }, widgetAutoRefreshIntervalMs); // Configurable interval (default 5 minutes)

    // Cleanup interval on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.walletAddress, isAuthenticated]);

  const isLoading = (loading || isLoadingAccount || isLoadingCoinbase || isLoadingPositions) && !timeoutError;
  // Only show full error if all queries failed or there's an internal error
  // If we have partial data, don't show full error (individual rows will show "Error")
  const hasAnyData = userAccount || coinbaseHoldings || positionGroups;
  const allQueriesFailed = errorAccount && errorCoinbase && errorPositions && !hasAnyData;
  // Handle different error types: errorAccount/errorCoinbase are Error | null, errorPositions/error are string | null
  const getErrorMessage = (err: Error | string | null | undefined): string | undefined => {
    if (!err) return undefined;
    if (typeof err === 'string') return err;
    return err.message;
  };
  const errorMsg = (allQueriesFailed || error)
    ? (getErrorMessage(errorAccount) || getErrorMessage(errorCoinbase) || getErrorMessage(errorPositions) || error || undefined)
    : undefined;


  if (timeoutError) {
    return (
      <div className={`dashboard-widget investment-summary ${className}`}>
        <div className="widget-header">
          <h3 className="widget-title">Investment Summary</h3>
        </div>
        <div className="widget-content">
          <div className="error-container">
            <p className="error-message">
              Request Timeout - Unable to load data<br />
              Please try refreshing the page, and contact an Administrator if the issue does not resolve itself.
            </p>
            <button
              onClick={() => {
                setTimeoutError(false);
                setLoading(true);
                window.location.reload();
              }}
              className="retry-button"
            >
              Refresh Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`dashboard-widget investment-summary ${className}`}>
        <div className="widget-header">
          <h3 className="widget-title">Investment Summary</h3>
        </div>
        <div className="widget-content">
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading your balances...</p>
          </div>
        </div>
        <div className="widget-refresh-bottom">
          <button
            className="refresh-button"
            disabled={true}
            style={{ pointerEvents: 'none' }}
          >
            Refresh
          </button>
          {lastUpdated && (
            <span className="last-updated">
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <DashboardWidget
        title="Investment Summary"
        loading={false}
        error={errorMsg || undefined}
        className={`investment-summary ${className}`}
        onRefresh={isLoading ? undefined : refreshData}
        refreshing={refreshing || isLoading}
        lastUpdated={lastUpdated ?? undefined}
        refreshButtonBottom={true}
      >
        <TableWidget
          rows={rows}
          showTokenColumn={false}
          showAmountColumn={false}
          columnHeaders={{
            label: 'Line Item',
            value: 'Value'
          }}
        />
      </DashboardWidget>

      <InfoModal
        isOpen={walletModalOpen}
        onClose={() => setWalletModalOpen(false)}
        title="Wallet Balance"
        className="wallet-balance-modal"
      >
        <TokenBalancesWidget />
      </InfoModal>

      <InfoModal
        isOpen={poolModalOpen}
        onClose={() => setPoolModalOpen(false)}
        title="Pool Balance"
      >
        <PoolPositionsWidget />
      </InfoModal>
      <InfoModal
        isOpen={coinbaseModalOpen}
        onClose={() => setCoinbaseModalOpen(false)}
        title="Coinbase Balance"
        className="coinbase-balance-modal"
      >
        <CoinbaseBalancesWidget />
      </InfoModal>
    </>
  );
};
